<template>
  <div class="platform-releases-page container-fluid py-4">
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── 1. VIEW: MANAGED PLATFORMS DASHBOARD (Regression Library Grid) ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-if="currentView === 'dashboard'">
      <!-- Page Header -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3">
            <div class="ms-page-header__icon">
              <i class="fa-solid fa-layer-group"></i>
            </div>
            <div>
              <p class="ms-page-header__breadcrumb">Platform Delivery <span class="ms-breadcrumb-sep">/</span> Platform Releases Hub</p>
              <h1 class="ms-page-header__title">Platform Releases Hub</h1>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button 
              class="btn btn-cyan-primary fw-bold px-3 py-2 text-sm d-flex align-items-center gap-2"
              data-bs-toggle="modal" 
              data-bs-target="#platformFormModal" 
              @click="openAddPlatformModal"
            >
              <i class="fa-solid fa-plus"></i>
              <span>Register New Platform</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Search & Filters Toolbar -->
      <div class="card border-0 shadow-sm bg-white mb-4 filter-toolbar-card">
        <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div class="d-flex align-items-center gap-2 flex-grow-1" style="max-width: 480px;">
            <div class="input-group input-group-sm">
              <span class="input-group-text bg-light border-end-0 text-muted-custom">
                <i class="fa-solid fa-magnifying-glass"></i>
              </span>
              <input 
                type="text" 
                class="form-control bg-light border-start-0 text-xs" 
                v-model="platformSearchQuery" 
                placeholder="Search managed platforms by name, slug, or description..." 
              />
              <button v-if="platformSearchQuery" @click="platformSearchQuery = ''" class="btn btn-light border border-start-0 text-muted-custom text-xs">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>
          </div>

          <div class="d-flex align-items-center gap-3 text-muted-custom text-xs fw-semibold">
            <span><i class="fa-solid fa-server me-1 text-info"></i> {{ filteredPlatforms.length }} Platforms Active</span>
            <span><i class="fa-solid fa-clock-rotate-left me-1 text-success"></i> Centralized Changelogs</span>
          </div>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="isLoadingPlatforms" class="text-center py-5">
        <div class="spinner-border text-info" role="status" style="width: 3rem; height: 3rem;">
          <span class="visually-hidden">Loading platforms...</span>
        </div>
        <p class="text-muted-custom mt-3 fw-bold">Loading your managed platforms dashboard...</p>
      </div>

      <!-- Platforms Grid (Regression Library Style) -->
      <div v-else>
        <!-- Empty State -->
        <div v-if="filteredPlatforms.length === 0" class="card shadow-sm border-0 bg-white p-5 text-center text-muted">
          <i class="fa-solid fa-layer-group fa-3x mb-3 text-light"></i>
          <h5 class="fw-bold text-dark">No Platforms Found</h5>
          <p class="small mb-3">Register your first platform or microservice to start tracking and publishing release notes.</p>
          <div>
            <button class="btn btn-cyan-primary btn-sm fw-bold px-3 py-2" data-bs-toggle="modal" data-bs-target="#platformFormModal" @click="openAddPlatformModal">
              <i class="fa-solid fa-plus me-1"></i> Register Platform
            </button>
          </div>
        </div>

        <!-- 3-Column Cards Grid -->
        <div v-else class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-5">
          <div v-for="plat in filteredPlatforms" :key="plat.id" class="col">
            <div class="card h-100 border-0 shadow-sm project-card bg-white" @click="selectPlatformView(plat)">
              <div class="card-body p-4 d-flex flex-column justify-content-between">
                <div>
                  <!-- Card Header: Custom Logo / Icon + Settings & Trash Actions -->
                  <div class="d-flex justify-content-between align-items-start mb-3">
                    <div 
                      class="project-icon-wrapper d-flex align-items-center justify-content-center rounded-3" 
                      :style="{ 
                        backgroundColor: isUrl(plat.icon) ? '#ffffff' : hexToRgba(plat.primary_color || '#00a0c1', 0.1), 
                        color: plat.primary_color || '#00a0c1', 
                        border: isUrl(plat.icon) ? '1px solid rgba(0,0,0,0.08)' : '1px solid ' + hexToRgba(plat.primary_color || '#00a0c1', 0.2),
                        width: '48px',
                        height: '48px',
                        fontSize: '20px'
                      }"
                    >
                      <img v-if="isUrl(plat.icon)" :src="plat.icon" class="project-card-logo" alt="" />
                      <i v-else :class="plat.icon || 'fa-solid fa-layer-group'"></i>
                    </div>

                    <!-- Actions (Settings & Delete) -->
                    <div class="d-flex align-items-center gap-1">
                      <button 
                        class="btn btn-sm btn-link text-info border-0 p-1 card-settings-btn" 
                        @click.stop="openEditPlatformModal(plat)" 
                        data-bs-toggle="modal"
                        data-bs-target="#editPlatformModal"
                        title="Edit Platform Settings & Logo"
                      >
                        <i class="fa-solid fa-gear fs-6"></i>
                      </button>
                      <button 
                        class="btn btn-sm btn-link text-danger border-0 p-1 card-delete-btn" 
                        @click.stop="confirmDeletePlatform(plat)" 
                        data-bs-toggle="modal"
                        data-bs-target="#deletePlatformModal"
                        title="Delete Platform"
                      >
                        <i class="fa-solid fa-trash-can fs-6"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Title & Slug -->
                  <div class="d-flex align-items-center gap-2 mb-2">
                    <h4 class="fw-bold text-dark mb-0 card-project-title">{{ plat.name }}</h4>
                    <span class="badge bg-light text-secondary border text-xxs font-monospace">{{ plat.key }}</span>
                  </div>
                  <p class="text-muted-custom small text-clamp mb-3" style="min-height: 40px; line-height: 1.5;">
                    {{ plat.description || 'Microservice and application release tracking notes in Spark.' }}
                  </p>
                </div>

                <!-- Stats Progress Section (Regression Library Style) -->
                <div>
                  <div class="p-3 bg-light rounded-3 mb-3 d-flex flex-column" style="border: 1px dashed #dee2e6; gap: 10px;">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted-custom text-xs fw-semibold">Total Releases:</span>
                      <span class="badge bg-white text-dark-blue border text-xs fw-bold px-2.5 py-1">{{ plat.total_releases || 0 }} total</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted-custom text-xs fw-semibold">Latest Version:</span>
                      <span class="version-pill-highlight-sm font-monospace">
                        <i class="fa-solid fa-tag opacity-75"></i>
                        <span>{{ plat.latest_version || 'None' }}</span>
                      </span>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="text-muted-custom text-xs fw-semibold">Last Released:</span>
                      <span class="text-dark-blue text-xs fw-semibold">{{ formatDate(plat.latest_release_date) || 'Never' }}</span>
                    </div>
                  </div>

                  <!-- Footer Action Link -->
                  <div class="d-flex align-items-center justify-content-between pt-2 border-top border-light text-info fw-bold text-xs card-action-link">
                    <span>Open Releases Hub</span>
                    <i class="fa-solid fa-arrow-right-long card-arrow"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── 2. VIEW: PLATFORM WORKSPACE DETAIL (Releases, API, SemVer) ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <div v-else-if="currentView === 'platform'">
      <!-- Page Header with Back Button & Dropdown Switcher -->
      <div class="ms-page-header mb-4">
        <div class="ms-page-header__content d-flex justify-content-between align-items-center flex-wrap gap-3 w-100">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <button 
              class="btn btn-header-secondary btn-sm fw-bold px-3 py-2 rounded-3 d-flex align-items-center gap-1.5 shadow-xs" 
              @click="currentView = 'dashboard'"
              title="Return to All Platforms Grid"
            >
              <i class="fa-solid fa-arrow-left text-info"></i>
              <span>All Platforms</span>
            </button>

            <div class="d-flex align-items-center gap-3">
              <div class="ms-page-header__icon" :style="{ backgroundColor: currentPlatform ? hexToRgba(currentPlatform.primary_color || '#00a0c1', 0.12) : '#f0f9ff' }">
                <img v-if="currentPlatform && isUrl(currentPlatform.icon)" :src="currentPlatform.icon" style="width: 24px; height: 24px; object-fit: contain;" alt="" />
                <i v-else :class="currentPlatform ? currentPlatform.icon : 'fa-solid fa-layer-group'" :style="{ color: currentPlatform ? currentPlatform.primary_color : '#00a0c1' }"></i>
              </div>
              <div>
                <p class="ms-page-header__breadcrumb mb-2" style="margin-bottom: 8px !important;">Platform Delivery <span class="ms-breadcrumb-sep">/</span> Platform Releases</p>
                <div class="d-flex align-items-center flex-wrap" style="gap: 12px;">
                  <h1 class="ms-page-header__title mb-0">{{ currentPlatform ? currentPlatform.name : 'Platform Releases' }}</h1>
                  <span class="badge bg-light text-secondary border text-xs font-monospace px-2.5 py-1">{{ selectedPlatformKey }}</span>
                  <span v-if="latestVersion" class="version-pill-highlight-sm font-monospace">
                    <i class="fa-solid fa-tag opacity-75"></i>
                    <span>{{ latestVersion }}</span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Switcher Dropdown (Switch between platforms without going back) -->
            <div class="dropdown ms-2">
              <button 
                class="btn btn-white bg-white border shadow-xs rounded-3 px-2.5 py-1.5 d-flex align-items-center gap-2 text-dark-blue fw-bold text-xs platform-select-btn" 
                type="button" 
                id="platformDetailDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false" 
              >
                <span class="platform-color-dot" :style="{ backgroundColor: currentPlatform ? currentPlatform.primary_color : '#00a0c1' }"></span>
                <span>Switch Platform</span>
                <i class="fa-solid fa-chevron-down text-muted-custom text-xxs"></i>
              </button>
              <ul class="dropdown-menu dropdown-menu-end shadow border-0 p-2 rounded-3" aria-labelledby="platformDetailDropdown" style="min-width: 240px;">
                <li class="dropdown-header text-muted-custom text-xxs text-uppercase fw-bold px-2 py-1">Select Target Platform</li>
                <li v-for="plat in platforms" :key="plat.id">
                  <a 
                    class="dropdown-item rounded-2 py-1.5 px-2 text-xs d-flex align-items-center justify-content-between" 
                    :class="{ 'active bg-cyan-primary text-white': plat.key === selectedPlatformKey }"
                    href="#" 
                    @click.prevent="switchPlatform(plat.key)"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="platform-color-dot" :style="{ backgroundColor: plat.primary_color || '#00a0c1' }"></span>
                      <span class="fw-bold">{{ plat.name }}</span>
                    </div>
                    <span class="badge border font-monospace text-xxs" :class="plat.key === selectedPlatformKey ? 'bg-white text-dark' : 'bg-light text-secondary'">
                      {{ plat.key }}
                    </span>
                  </a>
                </li>
                <li class="border-top mt-2 pt-2">
                  <a 
                    class="dropdown-item rounded-2 text-info fw-bold py-1.5 px-2 text-xs d-flex align-items-center gap-2" 
                    href="#" 
                    data-bs-toggle="modal" 
                    data-bs-target="#platformFormModal"
                    @click="openAddPlatformModal"
                  >
                    <i class="fa-solid fa-plus-circle"></i>
                    <span>Register New Platform</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Header Action Buttons -->
          <div class="d-flex align-items-center gap-2">
            <button 
              @click="openEditPlatformModal(currentPlatform)"
              data-bs-toggle="modal"
              data-bs-target="#editPlatformModal"
              class="btn btn-header-secondary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm"
              title="Edit Platform Settings & Logo"
            >
              <i class="fa-solid fa-gear text-info"></i>
              <span>Platform Settings</span>
            </button>

            <button 
              data-bs-toggle="modal" 
              data-bs-target="#uploadMarkdownModal"
              @click="openUploadMarkdownModal" 
              class="btn btn-cyan-primary fw-bold d-flex align-items-center gap-2 px-3 py-2 text-sm"
              type="button"
            >
              <i class="fa-solid fa-cloud-arrow-up"></i>
              <span>Upload .md Changelog</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Navigation Tabs Inside Platform Detail -->
      <ul class="nav nav-tabs mb-4" id="platformDetailTabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'changelogs' }" 
            @click="activeTab = 'changelogs'" 
            type="button"
          >
            <i class="fa-solid fa-clock-rotate-left me-2"></i>Platform Releases & Changelogs
            <span class="badge bg-cyan-primary text-white ms-2 text-xxs px-2 py-0.5 rounded-pill">{{ changelogs.length }}</span>
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'api' }" 
            @click="activeTab = 'api'" 
            type="button"
          >
            <i class="fa-solid fa-code me-2"></i>API & Embed Generator
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button 
            class="nav-link px-4 py-3 fw-bold" 
            :class="{ active: activeTab === 'versioning' }" 
            @click="activeTab = 'versioning'" 
            type="button"
          >
            <i class="fa-solid fa-scale-balanced me-2"></i>Versioning Guide & Standards
          </button>
        </li>
      </ul>

      <!-- ── TAB 1: RELEASES & CHANGELOGS TIMELINE ── -->
      <div v-show="activeTab === 'changelogs'" class="tab-pane fade show active">
        <!-- Active Platform Banner Card -->
        <div v-if="currentPlatform" class="card border-0 shadow-sm bg-white mb-4 platform-banner-card">
          <div class="card-body p-3.5 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div class="d-flex align-items-center gap-3">
              <div 
                class="platform-icon-avatar" 
                :style="{ 
                  backgroundColor: isUrl(currentPlatform.icon) ? '#ffffff' : hexToRgba(currentPlatform.primary_color || '#00a0c1', 0.12), 
                  color: currentPlatform.primary_color || '#00a0c1',
                  border: isUrl(currentPlatform.icon) ? '1px solid #e2e8f0' : 'none'
                }"
              >
                <img v-if="isUrl(currentPlatform.icon)" :src="currentPlatform.icon" class="platform-avatar-img" alt="" />
                <i v-else :class="currentPlatform.icon"></i>
              </div>
              <div>
                <div class="d-flex align-items-center flex-wrap" style="gap: 12px; margin-bottom: 8px;">
                  <h4 class="fw-bold mb-0 text-dark-blue">{{ currentPlatform.name }}</h4>
                  <span class="badge bg-light text-secondary border text-xs font-monospace px-2.5 py-1">{{ currentPlatform.key }}</span>
                  <span v-if="latestVersion" class="version-pill-highlight-sm font-monospace">
                    <i class="fa-solid fa-tag opacity-75"></i>
                    <span>Latest: {{ latestVersion }}</span>
                  </span>
                </div>
                <p class="text-muted-custom text-xs mb-0" style="margin-top: 6px; line-height: 1.6;">{{ currentPlatform.description || 'Centralized release notes for this platform.' }}</p>
              </div>
            </div>

            <div class="d-flex align-items-center gap-2 flex-wrap">
              <span v-if="latestVersion" class="badge bg-white text-dark-blue border text-xs px-3 py-2 fw-bold shadow-xs">
                <i class="fa-solid fa-tag text-info me-1.5"></i> Current Version: <span class="text-cyan-primary fw-bolder">{{ latestVersion }}</span>
              </span>
              <span class="badge bg-light text-dark-blue border text-xs px-3 py-2 fw-semibold">
                <i class="fa-solid fa-layer-group me-1.5 text-info"></i> {{ filteredChangelogs.length }} Published Releases
              </span>
              <button 
                @click="activeTab = 'api'" 
                class="btn btn-cyan-outline btn-xs rounded-pill px-3 py-1.5 fw-bold"
              >
                <i class="fa-solid fa-terminal me-1 text-info"></i> View Embed & API
              </button>
            </div>
          </div>
        </div>

        <!-- Filter Toolbar Card -->
        <div class="card border-0 shadow-sm bg-white mb-4 filter-toolbar-card">
          <div class="card-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <!-- Category Segmented Buttons -->
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <span class="text-muted-custom text-xs fw-bold text-uppercase me-1">Filter:</span>
              <div class="btn-group category-pills-group p-1 rounded-3" role="group">
                <button 
                  v-for="cat in categoryFilters" 
                  :key="cat.value" 
                  @click="selectedCategory = cat.value" 
                  class="btn btn-xs fw-bold px-3 py-1.5 rounded-2"
                  :class="{ 'active-filter': selectedCategory === cat.value }"
                >
                  <i :class="cat.icon" class="me-1"></i>
                  <span>{{ cat.label }}</span>
                </button>
              </div>
            </div>

            <!-- Search & Release Type Filter -->
            <div class="d-flex align-items-center gap-2 flex-grow-1 justify-content-end" style="max-width: 480px;">
              <div class="position-relative flex-grow-1">
                <i class="fa-solid fa-magnifying-glass text-muted-custom search-icon"></i>
                <input 
                  type="text" 
                  class="form-control form-control-sm ps-4 pe-4 bg-light border-0 focus-glow text-xs" 
                  v-model="searchQuery" 
                  placeholder="Search releases, titles, modules, features..." 
                />
                <button 
                  v-if="searchQuery" 
                  @click="searchQuery = ''" 
                  class="btn btn-link btn-xs text-muted-custom position-absolute end-0 top-50 translate-middle-y me-1 p-0 text-decoration-none"
                >
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </div>

              <select class="form-select form-select-sm bg-light border-0 text-xs fw-semibold" v-model="selectedReleaseType" style="width: 140px;">
                <option value="">All Types</option>
                <option value="major">Major (vX.0.0)</option>
                <option value="minor">Minor (v0.X.0)</option>
                <option value="patch">Patch (v0.0.X)</option>
                <option value="hotfix">Hotfix</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoadingChangelogs" class="text-center py-5">
          <div class="spinner-border text-info" role="status" style="width: 2.5rem; height: 2.5rem;">
            <span class="visually-hidden">Loading releases...</span>
          </div>
          <p class="text-muted-custom text-xs mt-3 fw-semibold">Fetching release notes...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredChangelogs.length === 0" class="card border-0 shadow-sm bg-white p-5 text-center my-4">
          <div class="empty-state-icon mx-auto mb-3 text-muted">
            <i class="fa-solid fa-box-open fa-3x text-light"></i>
          </div>
          <h5 class="fw-bold text-dark-blue mb-1">No Release Notes Found</h5>
          <p class="text-muted-custom text-xs mb-4" style="max-width: 420px; margin: 0 auto;">
            {{ searchQuery || selectedCategory || selectedReleaseType ? 'No changelog matches your current search filters.' : 'There are no release notes registered for ' + (currentPlatform ? currentPlatform.name : 'this platform') + ' yet.' }}
          </p>
          <div class="d-flex justify-content-center gap-2">
            <button 
              data-bs-toggle="modal" 
              data-bs-target="#uploadMarkdownModal"
              @click="openUploadMarkdownModal" 
              class="btn btn-cyan-primary btn-sm fw-bold px-3 py-2"
            >
              <i class="fa-solid fa-cloud-arrow-up me-1"></i> Upload .md Changelog
            </button>
          </div>
        </div>

        <!-- Changelogs Timeline Feed -->
        <div v-else class="changelog-timeline-feed">
          <div 
            v-for="release in filteredChangelogs" 
            :key="release.id" 
            class="card border-0 shadow-sm bg-white mb-4 changelog-card overflow-hidden"
          >
            <!-- Release Header -->
            <div class="card-header bg-white border-bottom p-3.5 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="d-flex align-items-center gap-3 flex-wrap">
                <span class="version-pill-highlight">
                  <i class="fa-solid fa-tag opacity-75"></i>
                  <span>{{ release.version }}</span>
                </span>
                <h5 class="fw-bold mb-0 text-dark-blue me-1">{{ release.title }}</h5>
                <span class="text-muted-custom opacity-50 d-none d-sm-inline">·</span>
                <span class="text-muted-custom text-xs d-inline-flex align-items-center gap-1.5 ms-1">
                  <i class="fa-regular fa-calendar opacity-75"></i>
                  <span>{{ formatDate(release.release_date) }}</span>
                </span>
              </div>

              <div class="d-flex align-items-center gap-2">
                <span 
                  class="badge text-uppercase text-xxs fw-bold px-2.5 py-1 rounded-pill"
                  :class="getReleaseTypeClass(release.release_type)"
                >
                  {{ release.release_type || 'minor' }}
                </span>
                <button 
                  class="btn btn-outline-danger btn-xs px-2.5 py-1 delete-btn fw-semibold" 
                  data-bs-toggle="modal"
                  data-bs-target="#deleteReleaseModal"
                  @click="confirmDeleteRelease(release)"
                  title="Delete this changelog"
                >
                  <i class="fa-solid fa-trash-can me-1"></i> Delete
                </button>
              </div>
            </div>

            <!-- Release Summary -->
            <div class="card-body p-4">
              <div v-if="release.summary" class="summary-banner p-3 rounded-2 mb-3.5 text-xs text-dark-blue">
                <p class="mb-0 fw-semibold" style="line-height: 1.6;">{{ release.summary }}</p>
              </div>

              <!-- Categories / Items Breakdown -->
              <div v-if="release.items && release.items.length > 0" class="row g-3">
                <!-- Features -->
                <div v-if="hasCategoryItems(release.items, 'features')" class="col-12">
                  <div class="category-block p-3 rounded-3 bg-features-subtle border border-success-subtle">
                    <div class="d-flex align-items-center gap-2 mb-2 text-success fw-bold text-xs text-uppercase">
                      <i class="fa-solid fa-rocket"></i>
                      <span>New Features & Modules</span>
                    </div>
                    <ul class="mb-0 ps-3 text-xs text-dark-blue">
                      <li v-for="(item, idx) in getCategoryItems(release.items, 'features')" :key="idx" class="mb-2">
                        <span v-if="item.badge" class="badge bg-success bg-opacity-10 text-success border border-success-subtle me-1.5 font-monospace text-xxs">{{ item.badge }}</span>
                        <strong>{{ item.title }}</strong>
                        <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Improvements -->
                <div v-if="hasCategoryItems(release.items, 'improvements')" class="col-12">
                  <div class="category-block p-3 rounded-3 bg-improvements-subtle border border-info-subtle">
                    <div class="d-flex align-items-center gap-2 mb-2 text-info-dark fw-bold text-xs text-uppercase">
                      <i class="fa-solid fa-bolt"></i>
                      <span>Enhancements & Optimizations</span>
                    </div>
                    <ul class="mb-0 ps-3 text-xs text-dark-blue">
                      <li v-for="(item, idx) in getCategoryItems(release.items, 'improvements')" :key="idx" class="mb-2">
                        <span v-if="item.badge" class="badge bg-info bg-opacity-10 text-info border border-info-subtle me-1.5 font-monospace text-xxs">{{ item.badge }}</span>
                        <strong>{{ item.title }}</strong>
                        <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Bug Fixes -->
                <div v-if="hasCategoryItems(release.items, 'fixes')" class="col-12">
                  <div class="category-block p-3 rounded-3 bg-fixes-subtle border border-warning-subtle">
                    <div class="d-flex align-items-center gap-2 mb-2 text-warning-dark fw-bold text-xs text-uppercase">
                      <i class="fa-solid fa-wrench"></i>
                      <span>Bug Fixes & Patches</span>
                    </div>
                    <ul class="mb-0 ps-3 text-xs text-dark-blue">
                      <li v-for="(item, idx) in getCategoryItems(release.items, 'fixes')" :key="idx" class="mb-2">
                        <span v-if="item.badge" class="badge bg-warning bg-opacity-10 text-warning-dark border border-warning-subtle me-1.5 font-monospace text-xxs">{{ item.badge }}</span>
                        <strong>{{ item.title }}</strong>
                        <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Security -->
                <div v-if="hasCategoryItems(release.items, 'security')" class="col-12">
                  <div class="category-block p-3 rounded-3 bg-security-subtle border border-purple-subtle">
                    <div class="d-flex align-items-center gap-2 mb-2 text-purple fw-bold text-xs text-uppercase">
                      <i class="fa-solid fa-shield-halved"></i>
                      <span>Security & Compliance</span>
                    </div>
                    <ul class="mb-0 ps-3 text-xs text-dark-blue">
                      <li v-for="(item, idx) in getCategoryItems(release.items, 'security')" :key="idx" class="mb-2">
                        <span v-if="item.badge" class="badge bg-purple bg-opacity-10 text-purple border border-purple-subtle me-1.5 font-monospace text-xxs">{{ item.badge }}</span>
                        <strong>{{ item.title }}</strong>
                        <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB 2: API & EMBED GENERATOR ── -->
      <div v-show="activeTab === 'api'" class="tab-pane fade show active">
        <div class="row g-4">
          <!-- Live Embed Preview -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm bg-white h-100">
              <div class="card-header bg-white border-bottom p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <h6 class="fw-bold mb-0 text-dark-blue">
                    <i class="fa-solid fa-window-maximize text-info me-2"></i> Live Embed Preview
                  </h6>
                  <span class="badge bg-success bg-opacity-10 text-success border border-success-subtle text-xxs fw-semibold">Live Preview</span>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button 
                    class="btn btn-outline-secondary btn-xs px-2.5 d-flex align-items-center gap-1.5" 
                    @click="fetchEmbedPreview"
                    title="Refresh preview content"
                  >
                    <i class="fa-solid fa-rotate-right text-info" :class="{ 'fa-spin': isEmbedLoading }"></i>
                    <span>Refresh</span>
                  </button>
                  <a 
                    :href="embedUrl" 
                    target="_blank" 
                    class="btn btn-cyan-outline btn-xs px-2.5 d-flex align-items-center gap-1.5 text-decoration-none fw-bold"
                    title="Open pure embed in a new browser tab"
                  >
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                    <span>Open in Tab</span>
                  </a>
                </div>
              </div>
              <div class="card-body p-3">
                <div class="embed-preview-wrapper border rounded-3 overflow-hidden bg-light position-relative" style="height: 540px;">
                  <div v-if="isEmbedLoading" class="position-absolute top-50 start-50 translate-middle text-center">
                    <div class="spinner-border spinner-border-sm text-info mb-2" role="status"></div>
                    <div class="text-xs text-muted-custom">Loading preview...</div>
                  </div>
                  <!-- Secure Direct Iframe using srcdoc (Guaranteed rendering across ports and domains) -->
                  <iframe 
                    :key="embedKey"
                    :srcdoc="embedHtmlDoc" 
                    class="w-100 h-100 border-0 bg-white" 
                    title="Changelog Live Embed"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>

          <!-- Code Snippets & Integration Methods -->
          <div class="col-lg-6">
            <div class="card border-0 shadow-sm bg-white h-100">
              <div class="card-header bg-white border-bottom p-3">
                <h6 class="fw-bold mb-0 text-dark-blue">
                  <i class="fa-solid fa-code text-info me-2"></i> Platform Integration Snippets
                </h6>
              </div>
              <div class="card-body p-3">
                <!-- Method 1: Styled HTML -->
                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="fw-bold text-xs text-dark-blue">1. Styled HTML Endpoint (For Modals & Drawers)</span>
                    <button class="btn btn-link btn-xs text-info p-0 text-decoration-none fw-bold" @click="copyCode(htmlSnippetCode)">
                      <i class="fa-regular fa-copy me-1"></i> Copy
                    </button>
                  </div>
                  <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 120px;">{{ htmlSnippetCode }}</pre>
                </div>

                <!-- Method 2: Iframe Embed -->
                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="fw-bold text-xs text-dark-blue">2. Pure Iframe Embed</span>
                    <button class="btn btn-link btn-xs text-info p-0 text-decoration-none fw-bold" @click="copyCode(iframeSnippetCode)">
                      <i class="fa-regular fa-copy me-1"></i> Copy
                    </button>
                  </div>
                  <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 100px;">{{ iframeSnippetCode }}</pre>
                </div>

                <!-- Method 3: JSON API -->
                <div class="mb-4">
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="fw-bold text-xs text-dark-blue">3. Public JSON API</span>
                    <button class="btn btn-link btn-xs text-info p-0 text-decoration-none fw-bold" @click="copyCode(jsonSnippetCode)">
                      <i class="fa-regular fa-copy me-1"></i> Copy
                    </button>
                  </div>
                  <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 100px;">{{ jsonSnippetCode }}</pre>
                </div>

                <!-- Method 4: CI/CD Webhook Upload -->
                <div>
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <span class="fw-bold text-xs text-dark-blue">4. Automated CI/CD Markdown Sync</span>
                    <button class="btn btn-link btn-xs text-info p-0 text-decoration-none fw-bold" @click="copyCode(cicdSnippetCode)">
                      <i class="fa-regular fa-copy me-1"></i> Copy
                    </button>
                  </div>
                  <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto" style="max-height: 100px;">{{ cicdSnippetCode }}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── TAB 3: VERSIONING & SEMVER GUIDE ── -->
      <div v-show="activeTab === 'versioning'" class="tab-pane fade show active">
        <div class="row g-4">
          <div class="col-lg-7">
            <div class="card border-0 shadow-sm bg-white p-4">
              <h5 class="fw-bold text-dark-blue mb-3">
                <i class="fa-solid fa-scale-balanced text-info me-2"></i> Semantic Versioning (SemVer) Standard
              </h5>
              <p class="text-xs text-muted-custom leading-relaxed mb-4">
                Spark and all connected platforms follow the standard <code>vMAJOR.MINOR.PATCH</code> semantic versioning specification. Each deployment must increment the appropriate segment based on the nature of the changes:
              </p>

              <div class="d-flex flex-column gap-3">
                <div class="p-3 rounded-3 bg-danger bg-opacity-10 border border-danger-subtle">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="badge bg-danger text-white font-monospace">MAJOR (vX.0.0)</span>
                    <strong class="text-dark-blue text-xs">Breaking Changes / Major Architectural Shifts</strong>
                  </div>
                  <p class="text-muted-custom text-xs mb-0">
                    Increment when making incompatible API changes, database schema breaking migrations, or retiring existing endpoints/modules.
                  </p>
                </div>

                <div class="p-3 rounded-3 bg-info bg-opacity-10 border border-info-subtle">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="badge bg-info text-white font-monospace">MINOR (v0.X.0)</span>
                    <strong class="text-dark-blue text-xs">New Features & Backwards-Compatible Additions</strong>
                  </div>
                  <p class="text-muted-custom text-xs mb-0">
                    Increment when adding new features, new UI screens, or functionality in a backwards-compatible manner.
                  </p>
                </div>

                <div class="p-3 rounded-3 bg-success bg-opacity-10 border border-success-subtle">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="badge bg-success text-white font-monospace">PATCH (v0.0.X)</span>
                    <strong class="text-dark-blue text-xs">Bug Fixes, Security Patches, Minor Tweaks</strong>
                  </div>
                  <p class="text-muted-custom text-xs mb-0">
                    Increment when delivering backwards-compatible bug fixes, UI adjustments, or security patches.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-5">
            <div class="card border-0 shadow-sm bg-white p-4 h-100">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="fw-bold text-dark-blue mb-0">
                  <i class="fa-solid fa-file-lines text-info me-2"></i> Standard Markdown Template
                </h5>
                <button class="btn btn-outline-secondary btn-xs fw-bold px-2.5" @click="copyCode(markdownTemplateCode)">
                  <i class="fa-regular fa-copy me-1"></i> Copy Template
                </button>
              </div>
              <p class="text-muted-custom text-xs mb-3">
                Use this structure for your <code>CHANGELOG.md</code> files before uploading or pushing through CI/CD pipelines:
              </p>
              <pre class="bg-dark text-light p-3 rounded-3 text-xs mb-0 font-monospace overflow-auto flex-grow-1" style="line-height: 1.5;">{{ markdownTemplateCode }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════════════════ -->
    <!-- ── 3. MODALS ── -->
    <!-- ══════════════════════════════════════════════════════════════════════ -->

    <!-- ── UPLOAD MARKDOWN MODAL ── -->
    <div 
      class="modal fade" 
      id="uploadMarkdownModal" 
      tabindex="-1" 
      aria-labelledby="uploadMarkdownModalLabel" 
      aria-hidden="true"
      ref="uploadMdModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable custom-modal-scrollable" style="margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <div class="d-flex align-items-center gap-2">
              <div class="modal-header-icon bg-info bg-opacity-10 text-info p-2 rounded-2">
                <i class="fa-solid fa-cloud-arrow-up"></i>
              </div>
              <div>
                <h5 class="modal-title fw-bold text-dark-blue mb-0" id="uploadMarkdownModalLabel">
                  Upload .md Changelog
                </h5>
                <p class="text-muted-custom text-xs mb-0">Deploy release notes instantly via Markdown file or paste</p>
              </div>
            </div>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref="closeUploadMdModal" @click="closeModalSafe('uploadMarkdownModal')"></button>
          </div>

          <div class="modal-body custom-modal-body p-4">
            <!-- Platform Target Selection -->
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Target Platform</label>
                <select class="form-select form-select-sm" v-model="uploadPlatformKey">
                  <option v-for="plat in platforms" :key="plat.id" :value="plat.key">
                    {{ plat.name }} ({{ plat.key }})
                  </option>
                </select>
              </div>
              <div class="col-md-6 d-flex align-items-end">
                <div class="form-text text-muted-custom text-xs">
                  Releases will be published and instantly live on this platform's embed and endpoints.
                </div>
              </div>
            </div>

            <!-- Drag & Drop Zone -->
            <div 
              class="dropzone-box p-4 border border-2 border-dashed rounded-3 text-center mb-3"
              :class="{ 'border-info bg-light': isDragging }"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleFileDrop"
            >
              <i class="fa-solid fa-file-code fa-2x text-info mb-2"></i>
              <p class="fw-bold text-dark-blue text-xs mb-1">Drag & Drop your <code>CHANGELOG.md</code> here</p>
              <p class="text-muted-custom text-xxs mb-2">Or click to select a markdown file from your computer</p>
              <label class="btn btn-outline-secondary btn-xs fw-bold px-3 cursor-pointer mb-0">
                <span>Browse File</span>
                <input type="file" accept=".md,.txt" class="d-none" @change="handleFileInput" />
              </label>
            </div>

            <!-- Direct Markdown Editor / Live Split Preview -->
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase d-flex justify-content-between">
                  <span>Raw Markdown Input</span>
                  <span class="text-info text-xxs fw-bold">Live Realtime Parsing</span>
                </label>
                <textarea 
                  class="form-control font-monospace text-xs" 
                  rows="11" 
                  v-model="rawMarkdownText"
                  @input="onMarkdownInput"
                  placeholder="# [v1.0.0] - 2026-09-07 - Release Title&#10;&#10;Summary overview of what was deployed...&#10;&#10;### Features&#10;- [Auth] Added SSO login support&#10;&#10;### Bug Fixes&#10;- Fixed session timeout issue"
                ></textarea>
              </div>

              <!-- Live Parsed Preview -->
              <div class="col-md-6">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase d-flex justify-content-between align-items-center">
                  <span>Live Parsed Preview</span>
                  <span v-if="parsedPreview" class="badge bg-success bg-opacity-10 text-success border border-success-subtle text-xxs font-monospace">
                    {{ parsedPreview.items ? parsedPreview.items.length : 0 }} Items
                  </span>
                </label>
                
                <div class="parsed-preview-box p-3 border rounded-3 bg-white overflow-auto text-xs shadow-xs" style="height: 245px;">
                  <!-- Dynamic Live Parsed Preview Content -->
                  <div v-if="parsedPreview && (parsedPreview.title || (parsedPreview.items && parsedPreview.items.length > 0))">
                    <!-- Version & Title Header -->
                    <div class="d-flex align-items-center justify-content-between flex-wrap gap-1 mb-2 pb-2 border-bottom">
                      <div class="d-flex align-items-center gap-1.5">
                        <span class="badge bg-cyan-primary text-white font-monospace text-xs px-2 py-0.5">
                          {{ parsedPreview.version || 'v1.0.0' }}
                        </span>
                        <strong class="text-dark-blue fs-6">{{ parsedPreview.title || 'Platform Release' }}</strong>
                      </div>
                      <span class="text-muted-custom text-xxs">
                        <i class="fa-regular fa-calendar me-1"></i>{{ parsedPreview.release_date || formatDate(new Date()) }}
                      </span>
                    </div>

                    <!-- Summary Banner -->
                    <div v-if="parsedPreview.summary" class="p-2 rounded-2 bg-light border-start border-3 border-info mb-2.5 text-xs text-dark-blue">
                      <p class="mb-0 text-muted-custom" style="line-height: 1.4;">{{ parsedPreview.summary }}</p>
                    </div>

                    <!-- Categorized Breakdown -->
                    <div v-if="parsedPreview.items && parsedPreview.items.length > 0" class="d-flex flex-column gap-2">
                      <!-- Features -->
                      <div v-if="hasCategoryItems(parsedPreview.items, 'features')" class="p-2 rounded-2 bg-features-subtle border border-success-subtle">
                        <div class="fw-bold text-success text-xxs text-uppercase mb-1">
                          <i class="fa-solid fa-rocket me-1"></i> Features
                        </div>
                        <ul class="mb-0 ps-3 text-xxs text-dark-blue">
                          <li v-for="(item, idx) in getCategoryItems(parsedPreview.items, 'features')" :key="idx" class="mb-1">
                            <span v-if="item.badge" class="badge bg-success bg-opacity-10 text-success border border-success-subtle me-1 font-monospace">{{ item.badge }}</span>
                            <strong>{{ item.title }}</strong>
                            <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                          </li>
                        </ul>
                      </div>

                      <!-- Improvements -->
                      <div v-if="hasCategoryItems(parsedPreview.items, 'improvements')" class="p-2 rounded-2 bg-improvements-subtle border border-info-subtle">
                        <div class="fw-bold text-info-dark text-xxs text-uppercase mb-1">
                          <i class="fa-solid fa-bolt me-1"></i> Improvements
                        </div>
                        <ul class="mb-0 ps-3 text-xxs text-dark-blue">
                          <li v-for="(item, idx) in getCategoryItems(parsedPreview.items, 'improvements')" :key="idx" class="mb-1">
                            <span v-if="item.badge" class="badge bg-info bg-opacity-10 text-info border border-info-subtle me-1 font-monospace">{{ item.badge }}</span>
                            <strong>{{ item.title }}</strong>
                            <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                          </li>
                        </ul>
                      </div>

                      <!-- Fixes -->
                      <div v-if="hasCategoryItems(parsedPreview.items, 'fixes')" class="p-2 rounded-2 bg-fixes-subtle border border-warning-subtle">
                        <div class="fw-bold text-warning-dark text-xxs text-uppercase mb-1">
                          <i class="fa-solid fa-wrench me-1"></i> Bug Fixes
                        </div>
                        <ul class="mb-0 ps-3 text-xxs text-dark-blue">
                          <li v-for="(item, idx) in getCategoryItems(parsedPreview.items, 'fixes')" :key="idx" class="mb-1">
                            <span v-if="item.badge" class="badge bg-warning bg-opacity-10 text-warning-dark border border-warning-subtle me-1 font-monospace">{{ item.badge }}</span>
                            <strong>{{ item.title }}</strong>
                            <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                          </li>
                        </ul>
                      </div>

                      <!-- Security -->
                      <div v-if="hasCategoryItems(parsedPreview.items, 'security')" class="p-2 rounded-2 bg-security-subtle border border-purple-subtle">
                        <div class="fw-bold text-purple text-xxs text-uppercase mb-1">
                          <i class="fa-solid fa-shield-halved me-1"></i> Security
                        </div>
                        <ul class="mb-0 ps-3 text-xxs text-dark-blue">
                          <li v-for="(item, idx) in getCategoryItems(parsedPreview.items, 'security')" :key="idx" class="mb-1">
                            <span v-if="item.badge" class="badge bg-purple bg-opacity-10 text-purple border border-purple-subtle me-1 font-monospace">{{ item.badge }}</span>
                            <strong>{{ item.title }}</strong>
                            <span v-if="item.description" class="text-secondary ms-1">— {{ item.description }}</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <!-- Empty Placeholder -->
                  <div v-else class="text-muted-custom text-center py-5 text-xs">
                    <i class="fa-solid fa-wand-magic-sparkles text-info fa-2x mb-2 d-block"></i>
                    <span>Type or drop a markdown file to preview real-time categorization.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('uploadMarkdownModal')">Cancel</button>
            <button 
              type="button" 
              class="btn btn-cyan-primary btn-sm fw-bold px-3 d-flex align-items-center gap-2"
              @click="submitUploadMarkdown"
              :disabled="!rawMarkdownText.trim() || isUploading"
            >
              <span v-if="isUploading" class="spinner-border spinner-border-sm"></span>
              <i v-else class="fa-solid fa-cloud-arrow-up"></i>
              <span>Publish Release</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── REGISTER PLATFORM MODAL ── -->
    <div 
      class="modal fade" 
      id="platformFormModal" 
      tabindex="-1" 
      aria-labelledby="platformFormModalLabel" 
      aria-hidden="true"
      ref="platformModal"
    >
      <div class="modal-dialog modal-dialog-scrollable custom-modal-scrollable" style="max-width: 580px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue" id="platformFormModalLabel">
              <i class="fa-solid fa-layer-group text-info me-2"></i> Register New Platform
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref="closePlatformModal" @click="closeModalSafe('platformFormModal')"></button>
          </div>

          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="saveNewPlatform">
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Platform Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="platformForm.name" 
                  @input="onPlatformNameInput"
                  placeholder="e.g. Auth Service, Core Platform, Driver App" 
                  required 
                />
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Platform Key / Slug *</label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-light text-muted-custom">/api/public/changelogs/</span>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="platformForm.key" 
                    placeholder="e.g. auth, core, xsurflog" 
                    required 
                  />
                </div>
                <span class="text-muted-custom text-xxs mt-1 d-block">Used in API & Embed endpoints for this service.</span>
              </div>

              <!-- Upload Custom Logo/Icon File (Regression Library Pattern) -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase d-flex justify-content-between align-items-center">
                  <span>Custom Brand Logo / Icon File</span>
                  <span class="text-info text-xxs">Optional</span>
                </label>
                <div class="p-3 border rounded bg-light bg-opacity-50">
                  <input type="file" accept="image/*" class="form-control form-control-sm bg-white border" @change="onIconFileChange($event, 'create')" />
                  <div v-if="platformForm.encoded_icon" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                    <div class="d-flex align-items-center gap-2">
                      <img :src="platformForm.preview_icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="Preview" />
                      <span class="small text-muted text-truncate" style="max-width: 180px;">{{ platformForm.icon_filename }}</span>
                    </div>
                    <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="platformForm.encoded_icon = ''; platformForm.icon_filename = ''; platformForm.preview_icon = ''">Clear</button>
                  </div>
                  <div class="form-text text-muted mt-1" style="font-size: 11px;">
                    Upload custom PNG, JPG, or SVG image. If uploaded, this logo will take precedence over FontAwesome icon.
                  </div>
                </div>
              </div>

              <!-- Choose Platform FontAwesome Icon Grid -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Choose FontAwesome Icon</label>
                <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-light bg-opacity-50">
                  <button 
                    v-for="ico in iconPresets" 
                    :key="ico" 
                    type="button" 
                    class="btn btn-sm" 
                    :class="(platformForm.icon === ico || (!platformForm.icon && ico === 'fa-solid fa-layer-group')) ? 'btn-cyan-primary text-white' : 'btn-white border bg-white text-secondary'" 
                    @click="platformForm.icon = ico" 
                    style="width: 38px; height: 38px;" 
                    :title="ico"
                  >
                    <i :class="ico"></i>
                  </button>
                </div>
              </div>

              <!-- Choose Theme Color -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Choose Theme Color</label>
                <div class="d-flex align-items-center gap-2 flex-wrap p-2 border rounded bg-light bg-opacity-50">
                  <input type="color" class="form-control form-control-color border-0" v-model="platformForm.primary_color" style="width: 38px; height: 34px;" />
                  <div class="d-flex gap-2 flex-wrap">
                    <button 
                      v-for="c in colorPresets" 
                      :key="c" 
                      type="button" 
                      @click="platformForm.primary_color = c" 
                      class="color-preset-btn rounded-circle border"
                      :style="{ backgroundColor: c }"
                      :class="{ active: platformForm.primary_color === c }"
                    ></button>
                  </div>
                </div>
              </div>

              <div class="mb-2">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Description</label>
                <textarea class="form-control" rows="2" v-model="platformForm.description" placeholder="Brief overview of what this platform / microservice does..."></textarea>
              </div>
            </form>
          </div>

          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('platformFormModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="saveNewPlatform" :disabled="isSavingPlatform">
              <span v-if="isSavingPlatform" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-check me-1"></i>
              <span>Create Platform</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── EDIT PLATFORM MODAL ── -->
    <div 
      class="modal fade" 
      id="editPlatformModal" 
      tabindex="-1" 
      aria-labelledby="editPlatformModalLabel" 
      aria-hidden="true"
      ref="editPlatformModal"
    >
      <div class="modal-dialog modal-dialog-scrollable custom-modal-scrollable" style="max-width: 580px; margin: 1.75rem auto;">
        <div class="modal-content border-0 shadow">
          <div class="modal-header bg-white border-bottom sticky-top">
            <h5 class="modal-title fw-bold text-dark-blue" id="editPlatformModalLabel">
              <i class="fa-solid fa-gear text-info me-2"></i> Edit Platform Settings
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" ref="closeEditPlatformModal" @click="closeModalSafe('editPlatformModal')"></button>
          </div>

          <div class="modal-body custom-modal-body p-4">
            <form @submit.prevent="saveEditPlatform">
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Platform Name *</label>
                <input 
                  type="text" 
                  class="form-control" 
                  v-model="editPlatformForm.name" 
                  placeholder="e.g. Auth Service" 
                  required 
                />
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Platform Key / Slug *</label>
                <div class="input-group input-group-sm">
                  <span class="input-group-text bg-light text-muted-custom">/api/public/changelogs/</span>
                  <input 
                    type="text" 
                    class="form-control" 
                    v-model="editPlatformForm.key" 
                    placeholder="e.g. auth" 
                    required 
                  />
                </div>
                <span class="text-muted-custom text-xxs mt-1 d-block">Changing the slug will update all associated release records.</span>
              </div>

              <!-- Upload Custom Logo/Icon File -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase d-flex justify-content-between align-items-center">
                  <span>Custom Brand Logo / Icon File</span>
                  <span class="text-info text-xxs">Optional</span>
                </label>
                <div class="p-3 border rounded bg-light bg-opacity-50">
                  <input type="file" accept="image/*" class="form-control form-control-sm bg-white border" @change="onIconFileChange($event, 'edit')" />
                  
                  <!-- Preview New File Selection -->
                  <div v-if="editPlatformForm.encoded_icon" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                    <div class="d-flex align-items-center gap-2">
                      <img :src="editPlatformForm.preview_icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="New Preview" />
                      <span class="small text-muted text-truncate" style="max-width: 180px;">{{ editPlatformForm.icon_filename }}</span>
                    </div>
                    <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="editPlatformForm.encoded_icon = ''; editPlatformForm.icon_filename = ''; editPlatformForm.preview_icon = ''">Clear</button>
                  </div>
                  
                  <!-- Show Current Logo if URL -->
                  <div v-else-if="isUrl(editPlatformForm.icon)" class="d-flex align-items-center justify-content-between mt-2 p-2 border rounded bg-white">
                    <div class="d-flex align-items-center gap-2">
                      <img :src="editPlatformForm.icon" style="width: 28px; height: 28px; object-fit: contain; border-radius: 4px;" alt="Current Icon" />
                      <span class="small text-muted">Current Brand Logo</span>
                    </div>
                    <button type="button" class="btn btn-sm btn-link text-danger p-0 border-0 text-decoration-none fw-bold small" @click="editPlatformForm.icon = 'fa-solid fa-layer-group'">Reset to Default Icon</button>
                  </div>

                  <div class="form-text text-muted mt-1" style="font-size: 11px;">
                    Upload custom PNG, JPG, or SVG image. If uploaded, this logo will take precedence over FontAwesome icon.
                  </div>
                </div>
              </div>

              <!-- Choose Platform FontAwesome Icon Grid -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Choose FontAwesome Icon</label>
                <div class="d-flex flex-wrap gap-2 p-2 border rounded bg-light bg-opacity-50">
                  <button 
                    v-for="ico in iconPresets" 
                    :key="ico" 
                    type="button" 
                    class="btn btn-sm" 
                    :class="(editPlatformForm.icon === ico || (!editPlatformForm.icon && ico === 'fa-solid fa-layer-group')) ? 'btn-cyan-primary text-white' : 'btn-white border bg-white text-secondary'" 
                    @click="editPlatformForm.icon = ico" 
                    style="width: 38px; height: 38px;" 
                    :title="ico"
                  >
                    <i :class="ico"></i>
                  </button>
                </div>
              </div>

              <!-- Choose Theme Color -->
              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Choose Theme Color</label>
                <div class="d-flex align-items-center gap-2 flex-wrap p-2 border rounded bg-light bg-opacity-50">
                  <input type="color" class="form-control form-control-color border-0" v-model="editPlatformForm.primary_color" style="width: 38px; height: 34px;" />
                  <div class="d-flex gap-2 flex-wrap">
                    <button 
                      v-for="c in colorPresets" 
                      :key="c" 
                      type="button" 
                      @click="editPlatformForm.primary_color = c" 
                      class="color-preset-btn rounded-circle border"
                      :style="{ backgroundColor: c }"
                      :class="{ active: editPlatformForm.primary_color === c }"
                    ></button>
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Description</label>
                <textarea class="form-control" rows="2" v-model="editPlatformForm.description" placeholder="Brief overview of what this platform / microservice does..."></textarea>
              </div>

              <!-- Platform API Key & CI/CD Token -->
              <div class="mb-2">
                <label class="form-label fw-bold text-secondary text-xs text-uppercase">Platform API Key (CI/CD Webhooks)</label>
                <div class="input-group input-group-sm">
                  <input type="text" class="form-control font-monospace text-xs bg-light" :value="editPlatformForm.api_key" readonly />
                  <button class="btn btn-outline-secondary" type="button" @click="copyCode(editPlatformForm.api_key)" title="Copy API Key">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                </div>
                <div class="form-check mt-2">
                  <input class="form-check-input" type="checkbox" id="regenerateKeyCheck" v-model="editPlatformForm.regenerate_api_key" />
                  <label class="form-check-label text-xs text-muted-custom" for="regenerateKeyCheck">
                    Regenerate a new API Key on save (invalidates existing CI/CD secret)
                  </label>
                </div>
              </div>
            </form>
          </div>

          <div class="modal-footer bg-light border-top">
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeModalSafe('editPlatformModal')">Cancel</button>
            <button type="button" class="btn btn-cyan-primary btn-sm fw-bold px-3" @click="saveEditPlatform" :disabled="isSavingPlatform">
              <span v-if="isSavingPlatform" class="spinner-border spinner-border-sm me-1"></span>
              <i v-else class="fa-solid fa-check me-1"></i>
              <span>Save Changes</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── DELETE PLATFORM CONFIRMATION MODAL ── -->
    <div 
      class="modal fade" 
      id="deletePlatformModal" 
      tabindex="-1" 
      aria-hidden="true"
      ref="deletePlatformModal"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content border-0 shadow text-center p-3">
          <div class="modal-body">
            <div class="delete-icon-box mx-auto mb-3">
              <i class="fa-solid fa-trash-can text-danger fa-2x"></i>
            </div>
            <h5 class="fw-bold text-dark mb-2">Delete Platform?</h5>
            <p class="text-muted-custom text-xs mb-4">
              Are you sure you want to delete platform <strong class="text-dark">{{ platformToDelete?.name }}</strong> (<code>{{ platformToDelete?.key }}</code>)? This will archive the platform from the hub.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="closeModalSafe('deletePlatformModal')">Cancel</button>
              <button 
                type="button" 
                class="btn btn-danger btn-sm fw-bold px-3 d-flex align-items-center gap-1.5" 
                @click="deletePlatform" 
                :disabled="isDeletingPlatform"
              >
                <span v-if="isDeletingPlatform" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fa-solid fa-trash-can"></i>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── DELETE RELEASE CONFIRMATION MODAL ── -->
    <div 
      class="modal fade" 
      id="deleteReleaseModal" 
      tabindex="-1" 
      aria-hidden="true"
      ref="deleteModal"
    >
      <div class="modal-dialog modal-sm modal-dialog-centered">
        <div class="modal-content border-0 shadow text-center p-3">
          <div class="modal-body">
            <div class="delete-icon-box mx-auto mb-3">
              <i class="fa-solid fa-trash-can text-danger fa-2x"></i>
            </div>
            <h5 class="fw-bold text-dark mb-2">Delete Release Note?</h5>
            <p class="text-muted-custom text-xs mb-4">
              Are you sure you want to remove release <strong>{{ selectedRelease?.version }}</strong> ({{ selectedRelease?.title }})? This action will archive the record.
            </p>
            <div class="d-flex justify-content-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm px-3" @click="closeModalSafe('deleteReleaseModal')">Cancel</button>
              <button 
                type="button" 
                class="btn btn-danger btn-sm fw-bold px-3 d-flex align-items-center gap-1.5" 
                @click="deleteRelease" 
                :disabled="isDeleting"
              >
                <span v-if="isDeleting" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fa-solid fa-trash-can"></i>
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal as BootstrapModal } from 'bootstrap';

export default {
  name: "PlatformReleasesDashboard",
  data() {
    return {
      currentView: "dashboard", // 'dashboard' (cards grid) | 'platform' (releases timeline & API)
      activeTab: "changelogs",   // 'changelogs' | 'api' | 'versioning'
      
      isLoadingPlatforms: false,
      isLoadingChangelogs: false,
      isDeleting: false,
      isDeletingPlatform: false,
      isUploading: false,
      isDragging: false,
      isSavingPlatform: false,
      isEmbedLoading: false,
      embedKey: 0,
      embedHtmlContent: "",

      // Platforms
      platforms: [],
      selectedPlatformKey: "spark",
      platformSearchQuery: "",

      // Changelogs List
      changelogs: [],

      // Filters
      searchQuery: "",
      selectedCategory: "",
      selectedReleaseType: "",
      categoryFilters: [
        { label: "All", value: "", icon: "fa-solid fa-list-ul" },
        { label: "Features", value: "features", icon: "fa-solid fa-rocket" },
        { label: "Bug Fixes", value: "fixes", icon: "fa-solid fa-wrench" },
        { label: "Improvements", value: "improvements", icon: "fa-solid fa-bolt" },
        { label: "Security", value: "security", icon: "fa-solid fa-shield-halved" },
      ],

      // Markdown Upload State & Real-Time Parser
      uploadPlatformKey: "spark",
      rawMarkdownText: "",
      parsedPreview: null,

      // Selected Release for Deletion
      selectedRelease: null,

      // Platform Form for Creation
      platformForm: {
        name: "",
        key: "",
        description: "",
        icon: "fa-solid fa-server",
        primary_color: "#00a0c1",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      },

      // Platform Form for Editing
      editPlatformForm: {
        id: null,
        name: "",
        key: "",
        description: "",
        icon: "fa-solid fa-server",
        primary_color: "#00a0c1",
        api_key: "",
        regenerate_api_key: false,
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      },

      // Platform to Delete
      platformToDelete: null,

      colorPresets: ["#00a0c1", "#6366f1", "#e11d48", "#0d9488", "#8b5cf6", "#d97706", "#2563eb", "#10b981"],
      iconPresets: [
        "fa-solid fa-layer-group",
        "fa-solid fa-server",
        "fa-solid fa-shield-halved",
        "fa-solid fa-chart-line",
        "fa-solid fa-globe",
        "fa-solid fa-mobile-screen",
        "fa-solid fa-database",
        "fa-solid fa-sparkles",
        "fa-solid fa-cloud",
        "fa-solid fa-code",
      ],
    };
  },

  watch: {
    rawMarkdownText: {
      immediate: true,
      handler(newVal) {
        this.parseMarkdownClient(newVal);
      },
    },
    activeTab(newVal) {
      if (newVal === 'api') {
        this.fetchEmbedPreview();
      }
    },
    selectedPlatformKey() {
      if (this.activeTab === 'api') {
        this.fetchEmbedPreview();
      }
    },
  },

  computed: {
    currentPlatform() {
      return this.platforms.find((p) => p.key === this.selectedPlatformKey) || this.platforms[0] || null;
    },

    latestVersion() {
      if (this.changelogs && this.changelogs.length > 0) {
        return this.changelogs[0].version;
      }
      if (this.currentPlatform && this.currentPlatform.latest_version && this.currentPlatform.latest_version !== "None") {
        return this.currentPlatform.latest_version;
      }
      return null;
    },

    apiBaseUrl() {
      let base = (this.$axios && this.$axios.defaults && this.$axios.defaults.baseURL) 
        ? this.$axios.defaults.baseURL 
        : (process.env.VUE_APP_API_BASE_URL || window.location.origin);
      if (base.endsWith("/")) {
        base = base.slice(0, -1);
      }
      return base;
    },

    filteredPlatforms() {
      if (!this.platformSearchQuery.trim()) {
        return this.platforms;
      }
      const q = this.platformSearchQuery.toLowerCase();
      return this.platforms.filter((p) => {
        return (
          (p.name || "").toLowerCase().includes(q) ||
          (p.key || "").toLowerCase().includes(q) ||
          (p.description || "").toLowerCase().includes(q)
        );
      });
    },

    filteredChangelogs() {
      return this.changelogs.filter((release) => {
        if (this.searchQuery.trim()) {
          const q = this.searchQuery.toLowerCase();
          const matchVersion = (release.version || "").toLowerCase().includes(q);
          const matchTitle = (release.title || "").toLowerCase().includes(q);
          const matchSummary = (release.summary || "").toLowerCase().includes(q);
          const matchItems = (release.items || []).some(
            (i) =>
              (i.title || "").toLowerCase().includes(q) ||
              (i.description || "").toLowerCase().includes(q) ||
              (i.badge || "").toLowerCase().includes(q)
          );
          if (!matchVersion && !matchTitle && !matchSummary && !matchItems) {
            return false;
          }
        }

        if (this.selectedReleaseType && release.release_type !== this.selectedReleaseType) {
          return false;
        }

        if (this.selectedCategory) {
          const hasCategory = (release.items || []).some(
            (i) => i.category === this.selectedCategory
          );
          if (!hasCategory) return false;
        }

        return true;
      });
    },

    embedUrl() {
      const pKey = this.selectedPlatformKey || "spark";
      return `${this.apiBaseUrl}/api/public/changelogs/${pKey}/embed`;
    },

    embedHtmlDoc() {
      if (this.embedHtmlContent) {
        return this.embedHtmlContent;
      }
      return this.generateEmbedHtml(this.currentPlatform, this.changelogs);
    },

    htmlSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      return `// 1. Fetch styled HTML from Spark
fetch('${this.apiBaseUrl}/api/public/changelogs/${pKey}/html')
  .then(res => res.text())
  .then(html => {
    // 2. Inject directly into your modal body or drawer
    document.getElementById('changelog-container').innerHTML = html;
  })
  .catch(err => console.error('Error loading changelogs:', err));`;
    },

    iframeSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      return `<iframe 
  src="${this.apiBaseUrl}/api/public/changelogs/${pKey}/embed" 
  width="100%" 
  height="600px" 
  frameborder="0" 
  style="border-radius: 8px; border: 1px solid #e2e8f0;">
</iframe>`;
    },

    jsonSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      return `// cURL Request:
curl -X GET "${this.apiBaseUrl}/api/public/changelogs/${pKey}"

// JavaScript (Axios / Fetch):
const res = await axios.get('${this.apiBaseUrl}/api/public/changelogs/${pKey}');
console.log('Published releases:', res.data.data);`;
    },

    cicdSnippetCode() {
      const pKey = this.selectedPlatformKey || "spark";
      const apiKey = this.currentPlatform ? this.currentPlatform.api_key : "YOUR_PLATFORM_API_KEY";
      return `# In your GitHub Actions / Bitbucket deploy step:
curl -X POST "${this.apiBaseUrl}/api/public/changelogs/${pKey}/upload-md" \
  -H "X-Platform-Key: ${apiKey}" \
  -F "file=@CHANGELOG.md"`;
    },

    markdownTemplateCode() {
      return `# [v2.5.0] - ${new Date().toISOString().split("T")[0]} - Platform Release Title

Summary overview explaining clearly what was fixed or introduced in this production deployment.

### Features
- [Module] New Capability: User-friendly description of the feature.
- One-Click Document Export: Description of performance or UI upgrade.

### Bug Fixes
- [Auth] Token Refresh Fix: Resolved token expiration timeout on reconnect.
- Expiration Scheduler: Corrected reminder notification delay.

### Improvements
- [UI] Enhanced Filter Toolbar: Added responsive single-row segmented buttons.

### Security
- Role-Based Permissions: Restricted administrative endpoints.`;
    },
  },

  mounted() {
    this.fetchPlatforms();
  },

  methods: {
    // ── Instant Client-Side Markdown Parser ──
    parseMarkdownClient(rawText) {
      if (!rawText || !rawText.trim()) {
        this.parsedPreview = null;
        return;
      }

      const trimmed = rawText.trim();
      const lines = trimmed.split('\n');
      
      let version = "v1.0.0";
      let title = "Platform Release";
      let releaseDate = new Date().toISOString().split('T')[0];
      let summary = "";
      let currentCategory = "features";
      let inHeader = true;
      const summaryLines = [];
      const items = [];

      const reVersionHeader = /^#{1,2}\s*\[?v?(\d+\.\d+\.\d+(?:-[a-z0-9.]+)?)\]?\s*(?:[-–—]\s*(\d{4}-\d{2}-\d{2}))?\s*(?:[-–—]\s*(.+))?/i;
      const reSimpleVersion = /^#{1,2}\s*(?:v?(\d+\.\d+\.\d+))\s*(?:[-–—:]\s*(.+))?/i;
      const reDate = /(\d{4}-\d{2}-\d{2})/;
      const reItemBadge = /^[-*+]\s*\[([^\]]+)\]\s*(.+)$/;
      const reItemSimple = /^[-*+]\s*(.+)$/;

      for (let i = 0; i < lines.length; i++) {
        const rawLine = lines[i];
        const line = rawLine.trim();
        if (!line) continue;

        // 1. Version Header Detection
        if (inHeader && (line.startsWith('# ') || line.startsWith('## '))) {
          const vMatch = line.match(reVersionHeader);
          if (vMatch && vMatch[1]) {
            version = 'v' + vMatch[1].replace(/^v/, '');
            if (vMatch[2]) releaseDate = vMatch[2];
            if (vMatch[3]) title = vMatch[3].trim();
            inHeader = false;
            continue;
          }
          const sMatch = line.match(reSimpleVersion);
          if (sMatch && sMatch[1]) {
            version = 'v' + sMatch[1].replace(/^v/, '');
            if (sMatch[2]) title = sMatch[2].trim();
            const dMatch = line.match(reDate);
            if (dMatch) releaseDate = dMatch[1];
            inHeader = false;
            continue;
          }
        }

        // 2. Category Section Headers
        if (line.startsWith('### ') || line.startsWith('## ')) {
          inHeader = false;
          const lower = line.toLowerCase();
          if (lower.includes('feat') || lower.includes('added') || lower.includes('new')) {
            currentCategory = 'features';
          } else if (lower.includes('fix') || lower.includes('bug') || lower.includes('patch') || lower.includes('resolved')) {
            currentCategory = 'fixes';
          } else if (lower.includes('improv') || lower.includes('change') || lower.includes('perf') || lower.includes('refactor') || lower.includes('enhanc')) {
            currentCategory = 'improvements';
          } else if (lower.includes('sec') || lower.includes('auth') || lower.includes('govern') || lower.includes('compliance')) {
            currentCategory = 'security';
          }
          continue;
        }

        // 3. Bullet Point Items
        if (line.startsWith('- ') || line.startsWith('* ') || line.startsWith('+ ')) {
          inHeader = false;
          let badge = '';
          let itemTitle = '';
          let desc = '';

          const badgeMatch = line.match(reItemBadge);
          if (badgeMatch) {
            badge = badgeMatch[1].trim();
            const rest = badgeMatch[2].trim();
            const colonIdx = rest.indexOf(':');
            if (colonIdx !== -1) {
              itemTitle = rest.substring(0, colonIdx).trim();
              desc = rest.substring(colonIdx + 1).trim();
            } else {
              itemTitle = rest;
            }
          } else {
            const simpleMatch = line.match(reItemSimple);
            if (simpleMatch) {
              const rest = simpleMatch[1].trim();
              const colonIdx = rest.indexOf(':');
              if (colonIdx !== -1) {
                itemTitle = rest.substring(0, colonIdx).trim();
                desc = rest.substring(colonIdx + 1).trim();
              } else {
                itemTitle = rest;
              }
            }
          }

          if (itemTitle) {
            items.push({
              category: currentCategory,
              badge: badge,
              title: itemTitle,
              description: desc,
            });
          }
          continue;
        }

        // 4. Summary Text (Non-bullet paragraph before or between categories)
        if (inHeader || items.length === 0) {
          summaryLines.push(line);
        }
      }

      if (summaryLines.length > 0) {
        summary = summaryLines.join(' ');
      } else if (items.length > 0) {
        summary = `Release ${version} delivers ${items.length} updates and enhancements.`;
      } else {
        summary = "General platform maintenance and stability updates.";
      }

      // Semantic version calculation
      const verClean = version.replace(/^v/, '');
      const parts = verClean.split('.');
      let releaseType = 'minor';
      if (parts.length >= 3) {
        if (parts[1] === '0' && parts[2] === '0') {
          releaseType = 'major';
        } else if (parts[2] === '0') {
          releaseType = 'minor';
        } else {
          releaseType = 'patch';
        }
      }

      this.parsedPreview = {
        version: version,
        title: title,
        release_date: releaseDate,
        release_type: releaseType,
        summary: summary,
        items: items,
      };
    },

    onMarkdownInput() {
      this.parseMarkdownClient(this.rawMarkdownText);
    },

    // ── Live Embed Preview Loader ──
    fetchEmbedPreview() {
      const pKey = this.selectedPlatformKey || "spark";
      this.isEmbedLoading = true;
      this.$axios
        .get(`/api/public/changelogs/${pKey}/embed`)
        .then((res) => {
          this.embedHtmlContent = typeof res.data === 'string' ? res.data : '';
          this.embedKey += 1;
        })
        .catch((err) => {
          console.error("Error loading embed preview:", err);
          // Fallback to client-generated template
          this.embedHtmlContent = this.generateEmbedHtml(this.currentPlatform, this.changelogs);
          this.embedKey += 1;
        })
        .finally(() => {
          this.isEmbedLoading = false;
        });
    },

    generateEmbedHtml(platform, logs) {
      const pName = platform ? platform.name : "Platform Releases";
      const pColor = platform && platform.primary_color ? platform.primary_color : "#00a0c1";
      
      let logoHtml = "";
      if (platform && platform.icon) {
        if (this.isUrl(platform.icon) || platform.icon.startsWith("data:") || platform.icon.startsWith("http") || platform.icon.startsWith("/")) {
          logoHtml = `<div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background: #ffffff; border: 1px solid #e2e8f0; padding: 3px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); flex-shrink: 0;"><img src="${platform.icon}" alt="" style="width: 100%; height: 100%; object-fit: contain; border-radius: 6px;" /></div>`;
        } else {
          logoHtml = `<div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 8px; background: rgba(0, 160, 193, 0.1); border: 1px solid #e2e8f0; flex-shrink: 0;"><i class="${platform.icon}" style="font-size: 18px; color: ${pColor};"></i></div>`;
        }
      }

      let bodyContent = "";

      if (!logs || logs.length === 0) {
        bodyContent = `<div class="card" style="text-align: center; color: #64748b; padding: 40px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0;">No release notes published yet.</div>`;
      } else {
        logs.forEach((c) => {
          let itemsHtml = "";
          let catsList = [];
          let fullSearchText = (c.version || "") + " " + (c.title || "") + " " + (c.summary || "") + " ";

          const cats = [
            { key: "features", label: "New Features", icon: "🚀", color: "#16a34a", bg: "rgba(34, 197, 94, 0.08)" },
            { key: "improvements", label: "Improvements", icon: "⚡", color: "#0284c7", bg: "rgba(14, 165, 233, 0.08)" },
            { key: "fixes", label: "Bug Fixes", icon: "🛠️", color: "#d97706", bg: "rgba(245, 158, 11, 0.08)" },
            { key: "security", label: "Security", icon: "🔒", color: "#8b5cf6", bg: "rgba(139, 92, 246, 0.08)" },
          ];

          cats.forEach((cat) => {
            const catItems = (c.items || []).filter((i) => i.category === cat.key);
            if (catItems.length > 0) {
              catsList.push(cat.key);
              itemsHtml += `
                <div class="cat-block" data-cat-key="${cat.key}" style="margin-top: 14px; background: ${cat.bg}; border-left: 3px solid ${cat.color}; padding: 12px 14px; border-radius: 6px;">
                  <div style="display: flex; align-items: center; gap: 6px; font-weight: 700; font-size: 11px; text-transform: uppercase; color: ${cat.color}; margin-bottom: 6px; letter-spacing: 0.5px;">
                    <span>${cat.icon}</span> <span>${cat.label}</span>
                  </div>
                  <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: #334155; line-height: 1.6;">
                    ${catItems
                      .map((i) => {
                        fullSearchText += (i.title || "") + " " + (i.description || "") + " " + (i.badge || "") + " ";
                        return `<li><strong>${i.title}</strong>${i.badge ? ` <span style="background: #ffffff; border: 1px solid #cbd5e1; font-size: 10px; padding: 2px 6px; border-radius: 4px; color: #475569; font-weight: 600;">${i.badge}</span>` : ""}${i.description ? ` <span style="color: #64748b;">— ${i.description}</span>` : ""}</li>`;
                      })
                      .join("")}
                  </ul>
                </div>
              `;
            }
          });

          const formattedDate = c.release_date ? new Date(c.release_date).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
          const typeColor = c.release_type === 'major' ? '#ef4444' : c.release_type === 'minor' ? '#0284c7' : '#10b981';

          bodyContent += `
            <div class="card" data-version="${c.version || ''}" data-type="${c.release_type || 'minor'}" data-categories="${catsList.join(',')}" data-search="${fullSearchText.toLowerCase()}" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 24px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,0.04);">
              <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #f1f5f9;">
                <div style="display: flex; align-items: center; gap: 14px;">
                  <span style="background: ${pColor}; color: #ffffff; padding: 5px 14px; border-radius: 20px; font-weight: 800; font-size: 13.5px; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; letter-spacing: 0.5px; display: inline-flex; align-items: center; gap: 8px; box-shadow: 0 2px 6px rgba(0,0,0,0.12);">
                    🏷️ <span>${c.version || 'v1.0.0'}</span>
                  </span>
                  <h3 style="margin: 0; font-size: 16px; font-weight: 700; color: #0f172a;">${c.title || 'Platform Release'}</h3>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 12px; color: #64748b;">📅 ${formattedDate}</span>
                  <span style="padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 700; text-transform: uppercase; background: ${typeColor}15; color: ${typeColor};">
                    ${c.release_type || 'minor'}
                  </span>
                </div>
              </div>
              ${c.summary ? `<div style="padding: 10px 14px; border-radius: 8px; background: #f8fafc; border-left: 3px solid ${pColor}; margin-bottom: 14px; font-size: 13px; color: #475569; line-height: 1.5;">${c.summary}</div>` : ''}
              ${itemsHtml}
            </div>
          `;
        });
      }

      return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pName} Releases</title>
  <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background-color: #f8fafc;
      color: #0f172a;
      margin: 0;
      padding: 20px;
      line-height: 1.5;
    }
    .container { max-width: 860px; margin: 0 auto; }
    .embed-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 16px;
      border-bottom: 2px solid #e2e8f0;
    }
    .embed-title {
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
    }

    /* Filter Toolbar */
    .filter-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
      margin-bottom: 20px;
    }
    .search-wrap {
      position: relative;
      flex: 1 1 240px;
      max-width: 360px;
    }
    .search-input {
      width: 100%;
      padding: 8px 32px 8px 34px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      background: #ffffff;
      color: #0f172a;
      font-size: 13.5px;
      outline: none;
      transition: all 0.2s ease;
    }
    .search-input:focus {
      border-color: ${pColor};
      box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
    }
    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #64748b;
      pointer-events: none;
    }
    .clear-btn {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #64748b;
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      padding: 2px;
    }
    .pills-wrap {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      align-items: center;
    }
    .cat-pill {
      padding: 5px 12px;
      border-radius: 20px;
      border: 1px solid #e2e8f0;
      background: #ffffff;
      color: #64748b;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.15s ease;
    }
    .cat-pill:hover {
      background: #f8fafc;
      color: #0f172a;
      border-color: ${pColor};
    }
    .cat-pill.active {
      background: ${pColor};
      color: #ffffff;
      border-color: ${pColor};
      font-weight: 700;
    }
    .release-count {
      font-size: 12px;
      color: #64748b;
      font-weight: 600;
    }
  </style>
</head>
<body>
<div class="container">
  <div class="embed-header">
    <div class="embed-title" style="display: flex; align-items: center; gap: 12px;">
      ${logoHtml}
      <span>${pName} Changelogs</span>
    </div>
    <div class="powered-badge" style="display: inline-flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 700; color: #64748b; background: #ffffff; border: 1px solid #e2e8f0; padding: 4px 12px; border-radius: 20px; box-shadow: 0 1px 2px rgba(0,0,0,0.03);">
      <span style="text-transform: uppercase; letter-spacing: 0.5px; font-size: 10px; opacity: 0.85;">Powered by</span>
      <div style="display: inline-flex; align-items: center; gap: 5px;">
        <svg width="12" height="15" viewBox="0 0 65 90" fill="none" xmlns="http://www.w3.org/2000/svg" style="display: block;">
          <path d="M16.8892 89.1421L52.1768 59.7479C64.4804 44.2444 51.2058 33.2613 51.2058 33.2613C44.0857 25.8332 33.0766 29.3828 33.0766 29.3828L30.1638 39.3949L42.7911 42.9498L24.3382 59.7479L16.8944 89.1421H16.8892Z" fill="#A3C0D1"/>
          <path d="M40.5151 0L5.22745 29.3942C-7.07623 44.8977 6.19838 55.8807 6.19838 55.8807C13.3185 63.3089 24.3276 59.7592 24.3276 59.7592L27.2404 49.7472L14.6131 46.1923L33.0712 29.3942L40.5151 0Z" fill="#00A0C1"/>
        </svg>
        <span style="font-weight: 900; font-size: 12px; color: #1e293b; letter-spacing: 0.8px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">SPARK</span>
      </div>
    </div>
  </div>

  <!-- Interactive Filter Toolbar -->
  <div class="filter-bar">
    <div class="search-wrap">
      <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
      </svg>
      <input type="text" id="embedSearch" class="search-input" placeholder="Search features, fixes, version..." oninput="filterReleases()" />
      <button type="button" id="clearSearchBtn" class="clear-btn" onclick="clearSearch()" style="display: none;">&times;</button>
    </div>
    <div class="pills-wrap">
      <button type="button" class="cat-pill active" onclick="setCategory('all', this)">All</button>
      <button type="button" class="cat-pill" onclick="setCategory('features', this)">🚀 Features</button>
      <button type="button" class="cat-pill" onclick="setCategory('fixes', this)">🛠️ Bug Fixes</button>
      <button type="button" class="cat-pill" onclick="setCategory('improvements', this)">⚡ Improvements</button>
      <button type="button" class="cat-pill" onclick="setCategory('security', this)">🔒 Security</button>
    </div>
    <div class="release-count" id="releaseCount">${(logs || []).length} releases</div>
  </div>

  ${bodyContent}
  <div id="noResultsMsg" class="card" style="display: none; text-align: center; color: #64748b; padding: 40px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0;">No release notes matching your search or filters.</div>
</div>

<script>
  var activeCategory = 'all';

  function filterReleases() {
    var searchInput = document.getElementById('embedSearch');
    var clearBtn = document.getElementById('clearSearchBtn');
    var query = (searchInput ? searchInput.value : '').toLowerCase().trim();
    if (clearBtn) {
      clearBtn.style.display = query ? 'block' : 'none';
    }

    var cards = document.querySelectorAll('.card[data-version]');
    var count = 0;

    cards.forEach(function(card) {
      var searchData = (card.getAttribute('data-search') || '').toLowerCase();
      var cats = (card.getAttribute('data-categories') || '').split(',');
      var matchQuery = !query || searchData.indexOf(query) !== -1;
      var matchCat = activeCategory === 'all' || cats.indexOf(activeCategory) !== -1;

      if (matchQuery && matchCat) {
        card.style.display = 'block';
        count++;
        var blocks = card.querySelectorAll('.cat-block');
        blocks.forEach(function(b) {
          if (activeCategory === 'all') {
            b.style.display = 'block';
          } else {
            var catKey = b.getAttribute('data-cat-key');
            b.style.display = (catKey === activeCategory) ? 'block' : 'none';
          }
        });
      } else {
        card.style.display = 'none';
      }
    });

    var noRes = document.getElementById('noResultsMsg');
    if (noRes) {
      noRes.style.display = (count === 0) ? 'block' : 'none';
    }

    var countEl = document.getElementById('releaseCount');
    if (countEl) {
      countEl.textContent = count + (count === 1 ? ' release' : ' releases');
    }
  }

  function setCategory(cat, el) {
    activeCategory = cat;
    var pills = document.querySelectorAll('.cat-pill');
    pills.forEach(function(p) { p.classList.remove('active'); });
    if (el) el.classList.add('active');
    filterReleases();
  }

  function clearSearch() {
    var searchInput = document.getElementById('embedSearch');
    if (searchInput) {
      searchInput.value = '';
    }
    filterReleases();
  }
${'<'}/script>
</body>
</html>`;
    },

    // ── Unified Toast Helper ──
    notify(type, msg) {
      if (this.$toast && typeof this.$toast[type] === 'function') {
        this.$toast[type](msg);
      } else if (this.toast && typeof this.toast[type] === 'function') {
        this.toast[type](msg);
      } else {
        console.log(`[${type.toUpperCase()}] ${msg}`);
      }
    },

    // ── Safe Modal Controls ──
    openModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        try {
          const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
          if (bootstrapObj && bootstrapObj.Modal) {
            const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
            modalInstance.show();
          }
        } catch (e) {
          console.error("Error opening modal programmatically:", e);
        }
      }
    },

    closeModalSafe(modalId) {
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          try {
            const bootstrapObj = (window.bootstrap && window.bootstrap.Modal) ? window.bootstrap : { Modal: BootstrapModal };
            if (bootstrapObj && bootstrapObj.Modal) {
              const modalInstance = bootstrapObj.Modal.getInstance(modalEl) || new bootstrapObj.Modal(modalEl);
              modalInstance.hide();
            }
          } catch (e) {
            console.error("Error closing modal programmatically:", e);
          }
        }
      }
      setTimeout(() => {
        const backdrops = document.querySelectorAll(".modal-backdrop");
        backdrops.forEach((el) => el.remove());
        document.body.classList.remove("modal-open");
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      }, 150);
    },

    isUrl(str) {
      if (!str || typeof str !== "string") return false;
      return str.startsWith("http://") || str.startsWith("https://") || str.startsWith("/public/");
    },

    hexToRgba(hex, alpha) {
      if (!hex || typeof hex !== "string" || !hex.startsWith("#")) {
        return `rgba(0, 160, 193, ${alpha || 0.1})`;
      }
      let c = hex.substring(1);
      if (c.length === 3) {
        c = c.split("").map((x) => x + x).join("");
      }
      const num = parseInt(c, 16);
      const r = (num >> 16) & 255;
      const g = (num >> 8) & 255;
      const b = num & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha || 0.1})`;
    },

    fetchPlatforms() {
      this.isLoadingPlatforms = true;
      this.$axios
        .get("/api/platform-guide/platforms")
        .then((response) => {
          if (response.data && response.data.success) {
            this.platforms = response.data.data || [];
            if (this.platforms.length > 0 && !this.platforms.some((p) => p.key === this.selectedPlatformKey)) {
              this.selectedPlatformKey = this.platforms[0].key;
            }
            this.uploadPlatformKey = this.selectedPlatformKey;
            if (this.currentView === 'platform') {
              this.fetchChangelogs();
              this.fetchEmbedPreview();
            }
          }
        })
        .catch((err) => {
          console.error("Error fetching platforms:", err);
        })
        .finally(() => {
          this.isLoadingPlatforms = false;
        });
    },

    selectPlatformView(plat) {
      if (!plat) return;
      this.selectedPlatformKey = plat.key;
      this.uploadPlatformKey = plat.key;
      this.currentView = "platform";
      this.activeTab = "changelogs";
      this.fetchChangelogs();
      this.fetchEmbedPreview();
    },

    switchPlatform(key) {
      this.selectedPlatformKey = key;
      this.uploadPlatformKey = key;
      this.fetchChangelogs();
      this.fetchEmbedPreview();
    },

    fetchChangelogs() {
      this.isLoadingChangelogs = true;
      this.$axios
        .get("/api/platform-guide/changelogs", {
          params: { platform: this.selectedPlatformKey },
        })
        .then((response) => {
          if (response.data && response.data.success) {
            this.changelogs = response.data.data || [];
          } else {
            this.changelogs = [];
          }
        })
        .catch((err) => {
          console.error("Error fetching changelogs:", err);
          this.changelogs = [];
        })
        .finally(() => {
          this.isLoadingChangelogs = false;
        });
    },

    // ── Icon / Logo File Upload ──
    onIconFileChange(event, type) {
      const file = event.target.files[0];
      if (!file) return;

      if (!file.type.startsWith("image/") && file.type !== "image/svg+xml") {
        this.notify("warning", "Please upload image files only (PNG, JPG, SVG).");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const rawResult = e.target.result;
        const base64Data = rawResult.split(",")[1] || rawResult;
        if (type === "create") {
          this.platformForm.encoded_icon = base64Data;
          this.platformForm.icon_filename = file.name;
          this.platformForm.preview_icon = rawResult;
        } else {
          this.editPlatformForm.encoded_icon = base64Data;
          this.editPlatformForm.icon_filename = file.name;
          this.editPlatformForm.preview_icon = rawResult;
        }
      };
      reader.readAsDataURL(file);
    },

    // ── Markdown Upload Modal Controls ──
    openUploadMarkdownModal() {
      this.uploadPlatformKey = this.selectedPlatformKey || (this.platforms[0] ? this.platforms[0].key : "spark");
      this.rawMarkdownText = "";
      this.parsedPreview = null;
      this.openModalSafe("uploadMarkdownModal");
    },

    handleFileInput(e) {
      const file = e.target.files[0];
      if (file) {
        this.readFile(file);
      }
    },

    handleFileDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files[0];
      if (file) {
        this.readFile(file);
      }
    },

    readFile(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.rawMarkdownText = e.target.result;
        this.parseMarkdownClient(this.rawMarkdownText);
      };
      reader.readAsText(file);
    },

    submitUploadMarkdown() {
      if (!this.rawMarkdownText.trim()) {
        this.notify("warning", "Please provide markdown content.");
        return;
      }

      this.isUploading = true;
      this.$axios
        .post("/api/platform-guide/changelogs/upload-markdown", {
          platform_key: this.uploadPlatformKey || this.selectedPlatformKey || "spark",
          markdown: this.rawMarkdownText,
        })
        .then((response) => {
          if (response.data && response.data.success) {
            this.notify("success", "Changelog published successfully!");
            this.closeModalSafe("uploadMarkdownModal");
            this.selectedPlatformKey = this.uploadPlatformKey || "spark";
            this.fetchChangelogs();
            this.fetchPlatforms();
            this.fetchEmbedPreview();
          } else {
            this.notify("error", response.data && response.data.message ? response.data.message : "Failed to publish changelog.");
          }
        })
        .catch((err) => {
          console.error("Upload markdown error:", err);
          const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "Failed to publish changelog.";
          this.notify("error", msg);
        })
        .finally(() => {
          this.isUploading = false;
        });
    },

    // ── Platform Registration Methods ──
    openAddPlatformModal() {
      this.platformForm = {
        name: "",
        key: "",
        description: "",
        icon: "fa-solid fa-server",
        primary_color: "#00a0c1",
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      };
      this.openModalSafe("platformFormModal");
    },

    onPlatformNameInput() {
      if (this.platformForm.name) {
        this.platformForm.key = this.platformForm.name
          .toLowerCase()
          .replace(/[^a-z0-9-_]/g, "-")
          .replace(/-+/g, "-");
      }
    },

    saveNewPlatform() {
      if (!this.platformForm.name.trim()) {
        this.notify("warning", "Please provide a platform name.");
        return;
      }
      if (!this.platformForm.key.trim()) {
        this.platformForm.key = this.platformForm.name
          .toLowerCase()
          .replace(/[^a-z0-9-_]/g, "-");
      }

      this.isSavingPlatform = true;
      this.$axios
        .post("/api/platform-guide/platforms", this.platformForm)
        .then((response) => {
          if (response.data && response.data.success) {
            this.notify("success", "Platform registered successfully!");
            this.closeModalSafe("platformFormModal");
            this.fetchPlatforms();
            if (response.data.data && response.data.data.key) {
              this.selectedPlatformKey = response.data.data.key;
              this.uploadPlatformKey = response.data.data.key;
            }
          } else {
            this.notify("error", response.data && response.data.message ? response.data.message : "Failed to register platform.");
          }
        })
        .catch((err) => {
          console.error("Save platform error:", err);
          const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "An error occurred.";
          this.notify("error", msg);
        })
        .finally(() => {
          this.isSavingPlatform = false;
        });
    },

    // ── Platform Edit Methods ──
    openEditPlatformModal(plat) {
      if (!plat) return;
      this.editPlatformForm = {
        id: plat.id,
        name: plat.name || "",
        key: plat.key || "",
        description: plat.description || "",
        icon: plat.icon || "fa-solid fa-server",
        primary_color: plat.primary_color || "#00a0c1",
        api_key: plat.api_key || "",
        regenerate_api_key: false,
        encoded_icon: "",
        icon_filename: "",
        preview_icon: "",
      };
      this.openModalSafe("editPlatformModal");
    },

    saveEditPlatform() {
      if (!this.editPlatformForm.name.trim()) {
        this.notify("warning", "Platform name is required.");
        return;
      }
      if (!this.editPlatformForm.id) {
        this.notify("error", "Platform ID is missing.");
        return;
      }

      this.isSavingPlatform = true;
      this.$axios
        .post(`/api/platform-guide/platforms/update/${this.editPlatformForm.id}`, this.editPlatformForm)
        .then((response) => {
          if (response.data && response.data.success) {
            this.notify("success", "Platform updated successfully!");
            this.closeModalSafe("editPlatformModal");
            this.fetchPlatforms();
            if (this.editPlatformForm.key) {
              this.selectedPlatformKey = this.editPlatformForm.key;
            }
            if (this.currentView === 'platform') {
              this.fetchChangelogs();
              this.fetchEmbedPreview();
            }
          } else {
            this.notify("error", response.data && response.data.message ? response.data.message : "Failed to update platform.");
          }
        })
        .catch((err) => {
          console.error("Error updating platform:", err);
          const msg = err.response && err.response.data && err.response.data.message ? err.response.data.message : "Error updating platform.";
          this.notify("error", msg);
        })
        .finally(() => {
          this.isSavingPlatform = false;
        });
    },

    // ── Platform Deletion Methods ──
    confirmDeletePlatform(plat) {
      this.platformToDelete = plat;
      this.openModalSafe("deletePlatformModal");
    },

    deletePlatform() {
      if (!this.platformToDelete) return;
      this.isDeletingPlatform = true;
      this.$axios
        .delete(`/api/platform-guide/platforms/${this.platformToDelete.id}`)
        .then((response) => {
          if (response.data && response.data.success) {
            this.notify("success", "Platform archived successfully.");
            this.closeModalSafe("deletePlatformModal");
            this.fetchPlatforms();
            if (this.selectedPlatformKey === this.platformToDelete.key) {
              this.selectedPlatformKey = "spark";
              this.currentView = "dashboard";
            }
          } else {
            this.notify("error", response.data && response.data.message ? response.data.message : "Failed to delete platform.");
          }
        })
        .catch((err) => {
          console.error("Error deleting platform:", err);
          this.notify("error", "Failed to delete platform.");
        })
        .finally(() => {
          this.isDeletingPlatform = false;
        });
    },

    // ── Delete Release Note Methods ──
    confirmDeleteRelease(release) {
      this.selectedRelease = release;
      this.openModalSafe("deleteReleaseModal");
    },

    deleteRelease() {
      if (!this.selectedRelease) return;
      this.isDeleting = true;
      this.$axios
        .delete(`/api/platform-guide/changelogs/${this.selectedRelease.id}`)
        .then((response) => {
          if (response.data && response.data.success) {
            this.notify("success", "Release note deleted successfully.");
            this.closeModalSafe("deleteReleaseModal");
            this.fetchChangelogs();
            this.fetchPlatforms();
            this.fetchEmbedPreview();
          } else {
            this.notify("error", response.data && response.data.message ? response.data.message : "Failed to delete release.");
          }
        })
        .catch((err) => {
          console.error("Error deleting release:", err);
          this.notify("error", "Failed to delete release.");
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },

    // ── Formatting & Category Helpers ──
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return dateStr;
      }
    },

    getReleaseTypeBadgeClass(type) {
      switch ((type || "").toLowerCase()) {
        case "major":
          return "bg-danger text-white";
        case "minor":
          return "bg-cyan-primary text-white";
        case "patch":
          return "bg-success text-white";
        case "hotfix":
          return "bg-warning text-dark";
        default:
          return "bg-info text-white";
      }
    },

    getReleaseTypeClass(type) {
      switch ((type || "").toLowerCase()) {
        case "major":
          return "bg-danger-subtle text-danger border border-danger-subtle";
        case "minor":
          return "bg-primary-subtle text-primary border border-primary-subtle";
        case "patch":
          return "bg-success-subtle text-success border border-success-subtle";
        case "hotfix":
          return "bg-warning-subtle text-warning-dark border border-warning-subtle";
        default:
          return "bg-info-subtle text-info border border-info-subtle";
      }
    },

    hasCategoryItems(items, category) {
      if (!items || !Array.isArray(items)) return false;
      return items.some((i) => i.category === category);
    },

    getCategoryItems(items, category) {
      if (!items || !Array.isArray(items)) return [];
      return items.filter((i) => i.category === category);
    },

    copyCode(text) {
      if (!navigator.clipboard) {
        this.notify("info", "Code snippet copied!");
        return;
      }
      navigator.clipboard.writeText(text).then(() => {
        this.notify("success", "Copied to clipboard!");
      });
    },
  },
};
</script>

<style scoped>
.platform-releases-page {
  background-color: transparent;
}

.text-dark-blue {
  color: #1e293b;
}

.text-muted-custom {
  color: #64748b;
}

.text-xxs {
  font-size: 11px;
}

.text-xs {
  font-size: 12px;
}

.text-sm {
  font-size: 13.5px;
}

/* ── Page Header Styles (Exact match with Regression Library) ── */
.ms-page-header {
  background: #ffffff;
  padding: 1.25rem 1.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  background-color: #f0f9ff;
  color: #00a0c1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
}

.ms-page-header__breadcrumb {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 600;
}

.ms-breadcrumb-sep {
  margin: 0 4px;
  color: #cbd5e1;
}

.ms-page-header__title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 0;
  line-height: 1.2;
}

/* ── Custom Styled Action Buttons ── */

/* ── Header Secondary Button & Outline Fix ── */
.btn-header-secondary {
  background-color: #ffffff !important;
  color: #334155 !important;
  border: 1px solid #cbd5e1 !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
}

.btn-header-secondary:hover,
.btn-header-secondary:focus,
.btn-header-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.06) !important;
}

.btn-header-secondary:hover i,
.btn-header-secondary:focus i,
.btn-header-secondary:active i {
  color: #008ca9 !important;
}

.btn-header-secondary:hover span,
.btn-header-secondary:focus span,
.btn-header-secondary:active span {
  color: #0f172a !important;
}

.btn-outline-secondary {
  background-color: #ffffff !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
  transition: all 0.15s ease-in-out !important;
}

.btn-outline-secondary:hover,
.btn-outline-secondary:focus,
.btn-outline-secondary:active {
  background-color: #f1f5f9 !important;
  color: #0f172a !important;
  border-color: #94a3b8 !important;
}

.btn-outline-secondary:hover i,
.btn-outline-secondary:focus i,
.btn-outline-secondary:active i {
  color: #008ca9 !important;
}

.btn-outline-secondary:hover span,
.btn-outline-secondary:focus span,
.btn-outline-secondary:active span {
  color: #0f172a !important;
}

.btn-cyan-primary {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  border: 1px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
  box-shadow: 0 2px 4px rgba(0, 160, 193, 0.2) !important;
}

.btn-cyan-primary:hover {
  background-color: #008ca9 !important;
  border-color: #008ca9 !important;
  color: #ffffff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 160, 193, 0.3) !important;
}

.btn-cyan-outline {
  background-color: #ffffff !important;
  color: #00a0c1 !important;
  border: 1.5px solid #00a0c1 !important;
  border-radius: 8px !important;
  transition: all 0.2s ease-in-out !important;
}

.btn-cyan-outline:hover {
  background-color: #e6f7fa !important;
  color: #008ca9 !important;
  border-color: #008ca9 !important;
  transform: translateY(-1px);
}

.bg-cyan-primary {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
}

.bg-cyan-subtle {
  background-color: rgba(0, 160, 193, 0.12) !important;
}

.text-cyan-primary {
  color: #00a0c1 !important;
}

.border-cyan-subtle {
  border-color: rgba(0, 160, 193, 0.3) !important;
}

.version-pill-highlight-sm {
  background: linear-gradient(135deg, #00a0c1 0%, #00738c 100%) !important;
  color: #ffffff !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important;
  font-size: 11.5px !important;
  font-weight: 800 !important;
  padding: 4px 12px !important;
  border-radius: 12px !important;
  letter-spacing: 0.5px !important;
  box-shadow: 0 1px 4px rgba(0, 160, 193, 0.3) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  line-height: 1.4 !important;
  vertical-align: middle !important;
}

.delete-btn {
  transition: all 0.2s ease;
}

.delete-btn:hover {
  background-color: #dc3545 !important;
  color: #ffffff !important;
  border-color: #dc3545 !important;
}

.delete-icon-box {
  width: 56px;
  height: 56px;
  background-color: #fef2f2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Platform Switcher & Badges */
.platform-color-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
}

.platform-dropdown-logo {
  width: 16px;
  height: 16px;
  object-fit: contain;
  border-radius: 3px;
}

.platform-avatar-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.platform-select-btn {
  transition: all 0.2s ease;
}

.platform-select-btn:hover {
  background-color: #f8fafc !important;
  border-color: #cbd5e1 !important;
}

.platform-banner-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.platform-icon-avatar {
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

/* ── Project Card (Exact match with Regression Library) ── */
.project-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  overflow: hidden;
  cursor: pointer;
}

.project-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08) !important;
  border-color: #00a0c1 !important;
}

.project-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  transition: transform 0.2s ease;
}

.project-card-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.card-project-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  transition: color 0.15s ease;
}

.project-card:hover .card-project-title {
  color: #00a0c1;
}

.card-settings-btn {
  color: #94a3b8;
  transition: color 0.15s ease, transform 0.15s ease;
}
.card-settings-btn:hover {
  color: #00a0c1 !important;
  transform: rotate(30deg);
}

.card-delete-btn {
  color: #cbd5e1;
  transition: color 0.15s ease, transform 0.15s ease;
}
.card-delete-btn:hover {
  color: #ef4444 !important;
  transform: scale(1.15);
}

.text-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-action-link {
  transition: color 0.15s ease;
}
.card-arrow {
  transition: transform 0.2s ease;
}
.project-card:hover .card-arrow {
  transform: translateX(5px);
}

/* ── Top-Level Tabs Custom Styling ── */
.nav-tabs {
  border-bottom: 2px solid #e9ecef !important;
}
.nav-tabs .nav-link {
  border: none !important;
  color: #6c757d !important;
  background: transparent !important;
  transition: all 0.2s ease;
  position: relative;
  padding: 12px 24px !important;
}
.nav-tabs .nav-link:hover {
  color: #00a0c1 !important;
}
.nav-tabs .nav-link.active {
  color: #00a0c1 !important;
  background: transparent !important;
}
.nav-tabs .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00a0c1;
  border-radius: 3px;
}

/* Filter Toolbar Card & Segmented Controls */
.filter-toolbar-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.category-pills-group {
  background-color: #f1f5f9;
  border: 1px solid #e2e8f0;
}

.category-pills-group .btn {
  font-size: 12px;
  border: none;
  transition: all 0.15s ease-in-out;
}

.category-pills-group .btn.active-filter {
  background-color: #00a0c1 !important;
  color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 160, 193, 0.25);
}

.category-pills-group .btn:not(.active-filter):hover {
  background-color: #e2e8f0 !important;
  color: #1e293b !important;
}

/* Changelog Timeline & Cards */
.changelog-card {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.changelog-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05) !important;
}

.version-badge {
  font-size: 13px;
  letter-spacing: 0.5px;
}

.version-pill-highlight {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #00a0c1 0%, #00738c 100%);
  color: #ffffff !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  padding: 5px 14px;
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;
  margin-right: 6px;
}

.version-pill-highlight:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 160, 193, 0.45);
}

.summary-banner {
  background: #f8fafc;
  border-left: 3px solid #00a0c1;
}

/* Change Categories */
.bg-features-subtle {
  background-color: #f0fdf4;
}

.bg-fixes-subtle {
  background-color: #fffbeb;
}

.bg-improvements-subtle {
  background-color: #f0f9ff;
}

.bg-security-subtle {
  background-color: #f5f3ff;
}

.text-warning-dark {
  color: #b45309;
}

.text-info-dark {
  color: #0369a1;
}

/* Search Glow */
.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 13px;
}

.focus-glow:focus {
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.15);
  border-color: #00a0c1 !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.dropzone-box {
  cursor: pointer;
  background-color: #f8fafc;
}

.color-preset-btn {
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: transform 0.15s ease;
}

.color-preset-btn:hover, .color-preset-btn.active {
  transform: scale(1.2);
  box-shadow: 0 0 0 2px #1e293b;
}

/* Custom Scrollable Modals - Guaranteed Visible Header & Footer */
.custom-modal-scrollable {
  max-height: calc(100vh - 2rem) !important;
  display: flex !important;
  flex-direction: column !important;
}

.custom-modal-scrollable .modal-content {
  max-height: calc(100vh - 2.5rem) !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 12px;
  overflow: hidden;
}

.custom-modal-scrollable .modal-header {
  flex-shrink: 0;
  background-color: #ffffff;
  z-index: 1055;
  border-bottom: 1px solid #e2e8f0;
}

.custom-modal-scrollable .custom-modal-body {
  flex: 1 1 auto;
  overflow-y: auto !important;
  max-height: calc(100vh - 160px) !important;
  padding: 1.25rem 1.5rem !important;
}

.custom-modal-scrollable .modal-footer {
  flex-shrink: 0;
  background-color: #f8fafc;
  z-index: 1055;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.03);
}
</style>
